// client/src/hooks/usePhaseClasses.js
import { useCallback, useEffect, useMemo, useState } from 'react';
import { usePhase } from '../contexts/PhaseContext';
import useAyPhaseReady from '../utils/useAyPhaseReady';
import { fetchPhaseClasses } from '../utils/fetchPhaseClasses';

/**
 * usePhaseClasses
 * Strict phase-filtered classes for dropdowns. Does not fall back across phases by default.
 */
export default function usePhaseClasses(options = {}) {
  const { allowEmptyFallback = false } = options;
  const { activePhase } = usePhase();
  const { ready, activePhase: phaseReady } = useAyPhaseReady();
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const phaseCode = useMemo(() => (phaseReady || activePhase || '').toString(), [phaseReady, activePhase]);

  const load = useCallback(async () => {
    if (!ready) return;
    setLoading(true);
    setError(null);
    try {
      const list = await fetchPhaseClasses(phaseCode, { allowEmptyFallback });
      setClasses(Array.isArray(list) ? list : []);
    } catch (e) {
      setError(e?.message || 'Failed to load classes');
      setClasses([]);
    } finally {
      setLoading(false);
    }
  }, [ready, phaseCode, allowEmptyFallback]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onPhaseChanged = () => load();
    window.addEventListener('app:phase:changed', onPhaseChanged);
    return () => window.removeEventListener('app:phase:changed', onPhaseChanged);
  }, [load]);

  return { classes, loading, error, reload: load };
}
